import {
  pushDataLayerEvent,
  usePostHubspotEvent,
  usePostHubspotProperty,
} from "@/features/analytics";
import { useLazyGetOrgInfoQuery } from "@api/accounts";
import { environment } from "@environment";
import { useAppSelector } from "@store";
import { getAccount } from "@store/account/selectors";
import moment from "moment";
import { useEffect, useRef, useState } from "react";

const useValidatePurchase = () => {
  const { mutate: postHubspotEvent } = usePostHubspotEvent();
  const { mutate: postHubspotProperty } = usePostHubspotProperty();
  const currentSubscription = useAppSelector(
    (s) => getAccount(s)?.subscriptionId,
  );
  const [getOrgInfo] = useLazyGetOrgInfoQuery();
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const [startTime] = useState(() => Date.now());
  const startTimeRef = useRef<number>(startTime);

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const account = await getOrgInfo({}).unwrap();

        if (account.subscriptionId !== currentSubscription) {
          updateHubspotContact({
            id: account.subscriptionPlanId,
            postHubspotProperty,
          });
          sendTimelineEvent({
            id: account.subscriptionPlanId,
            gclid: account.parameters?.gclid,
            postHubspotEvent,
          });
          setSuccess(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          return;
        }

        if (Date.now() - startTimeRef.current >= 5 * 60 * 1000) {
          setError(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          return;
        }

        timeoutRef.current = setTimeout(checkSubscription, 1000);
      } catch {
        setError(true);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      }
    };

    // Start the first check
    checkSubscription();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { error, success };
};

export default useValidatePurchase;

const getPaddlePlanData = (id: string) => {
  const paddlePlan = environment.paddle.plans.find((p) => p.id === id);
  if (!paddlePlan) return null;

  const monthlyValue =
    paddlePlan.period === "Monthly" ? paddlePlan.price : paddlePlan.price / 12;

  return {
    paddlePlan,
    monthlyValue,
  };
};

const updateHubspotContact = ({
  id,
  postHubspotProperty,
}: {
  id: string;
  postHubspotProperty: ReturnType<typeof usePostHubspotProperty>["mutate"];
}) => {
  const planData = getPaddlePlanData(id);
  if (!planData) return;

  const { paddlePlan, monthlyValue } = planData;

  postHubspotProperty({
    total_payment_amount: paddlePlan.price,
    number_of_seats: paddlePlan.seats,
    plan_billing_type: paddlePlan.period,
    plan_monthly_value: monthlyValue,
    plan_name: paddlePlan.title,
    trial_status: "inactive",
    success_paying_customer: moment(new Date()).utc().startOf("day").valueOf(),
  });
};

const sendTimelineEvent = ({
  id,
  gclid,
  postHubspotEvent,
}: {
  id: string;
  gclid?: string;
  postHubspotEvent: ReturnType<typeof usePostHubspotEvent>["mutate"];
}) => {
  const planData = getPaddlePlanData(id);
  if (!planData) return;

  const { paddlePlan, monthlyValue } = planData;

  const data: Record<string, unknown> = {
    plan: paddlePlan.title,
    billingPeriod: paddlePlan.period,
    monthlyValue,
    annualValue: paddlePlan.price,
  };
  if (gclid) data.gclid = gclid;
  postHubspotEvent({ event: "success paying customer", props: data });
  pushDataLayerEvent("conversion", {
    "event-action": "success paying customer",
    ...data,
  });
};
